'use client';

import { useLoading } from '@/context/LoadingContext';
import { useEffect, useState } from 'react';

export default function PageLoadingScreen() {
  const { hideLoading, isInitialLoad } = useLoading();
  const [displayLoading, setDisplayLoading] = useState(true);

  useEffect(() => {
    if (!isInitialLoad) {
      setDisplayLoading(false);
      return;
    }

    // Auto-hide loading after exactly 2 seconds
    const timer = setTimeout(() => {
      console.log('[PageLoadingScreen] Hiding loading screen');
      setDisplayLoading(false);
      hideLoading();
    }, 2000);

    return () => clearTimeout(timer);
  }, [isInitialLoad, hideLoading]);

  const brand = 'NITMiner';
  const suffix = 'Technologies';

  return (
    <>
      <style>{`
        .page-loading-overlay {
          position: fixed;
          inset: 0;
          width: 100%;
          height: 100%;
          background: radial-gradient(ellipse at 50% 40%, #1a1040 0%, #0a0a1f 45%, #05050d 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 99999;
          opacity: 1;
          visibility: visible;
          transition: opacity 0.6s ease-out, visibility 0.6s ease-out;
          pointer-events: auto;
          overflow: hidden;
          font-family: var(--font-inter, system-ui, sans-serif);
        }

        .page-loading-overlay.hidden {
          opacity: 0;
          visibility: hidden;
          pointer-events: none;
        }

        /* faint grid */
        .pl-grid {
          position: absolute;
          inset: 0;
          background-image: linear-gradient(rgba(129,140,248,0.07) 1px, transparent 1px),
            linear-gradient(90deg, rgba(129,140,248,0.07) 1px, transparent 1px);
          background-size: 48px 48px;
          -webkit-mask-image: radial-gradient(ellipse at center, black 20%, transparent 70%);
          mask-image: radial-gradient(ellipse at center, black 20%, transparent 70%);
          animation: pl-fade 1s ease-out both;
        }

        .pl-center {
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          padding: 0 16px;
        }

        /* logo with rotating gradient ring */
        .pl-logo-wrap {
          position: relative;
          width: 108px;
          height: 108px;
          display: grid;
          place-items: center;
          margin-bottom: 28px;
        }
        .pl-ring {
          position: absolute;
          inset: 0;
          border-radius: 50%;
          background: conic-gradient(from 0deg, #22d3ee, #6366f1, #a855f7, #ec4899, transparent 70%, #22d3ee);
          -webkit-mask: radial-gradient(farthest-side, transparent calc(100% - 3px), #000 calc(100% - 2px));
          mask: radial-gradient(farthest-side, transparent calc(100% - 3px), #000 calc(100% - 2px));
          animation: pl-spin 1.4s linear infinite, pl-fade 0.5s ease-out both;
        }
        .pl-glow {
          position: absolute;
          inset: -30px;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(99,102,241,0.45), transparent 65%);
          filter: blur(10px);
          animation: pl-pulse 1.6s ease-in-out infinite;
        }
        .pl-logo {
          position: relative;
          width: 64px;
          height: auto;
          animation: pl-logo-in 0.7s cubic-bezier(0.34, 1.56, 0.64, 1) both;
          filter: drop-shadow(0 0 14px rgba(99,102,241,0.7));
        }

        /* company name, letter by letter */
        .pl-name {
          position: relative;
          font-family: var(--font-poppins, 'Poppins', sans-serif);
          font-size: clamp(30px, 6vw, 52px);
          font-weight: 800;
          letter-spacing: -0.02em;
          line-height: 1.1;
          white-space: nowrap;
        }
        .pl-letter {
          display: inline-block;
          background: linear-gradient(110deg, #67e8f9 0%, #818cf8 22%, #e879f9 40%, #ffffff 50%, #67e8f9 60%, #818cf8 78%, #e879f9 100%);
          background-size: 250% 100%;
          background-position: 100% 50%;
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          opacity: 0;
          animation: pl-letter-in 0.55s cubic-bezier(0.22, 1, 0.36, 1) var(--d) forwards, pl-letter-shine 0.9s ease-in-out 1.1s both;
        }
        .pl-space { display: inline-block; width: 0.3em; }

        .pl-suffix {
          margin-top: 10px;
          font-family: var(--font-inter, system-ui, sans-serif);
          font-size: clamp(12px, 2.2vw, 16px);
          font-weight: 500;
          color: rgba(255,255,255,0.85);
          text-transform: uppercase;
          animation: pl-track 0.9s cubic-bezier(0.22, 1, 0.36, 1) 0.75s both;
        }

        .pl-tagline {
          margin-top: 18px;
          font-size: 12px;
          letter-spacing: 0.08em;
          color: rgba(165,180,252,0.75);
          animation: pl-up 0.6s ease-out 1.15s both;
        }

        /* progress bar synced to the 2s loader */
        .pl-bar {
          margin-top: 26px;
          width: 180px;
          height: 3px;
          border-radius: 999px;
          background: rgba(255,255,255,0.08);
          overflow: hidden;
          animation: pl-fade 0.4s ease-out 0.3s both;
        }
        .pl-bar > span {
          display: block;
          height: 100%;
          width: 100%;
          border-radius: inherit;
          background: linear-gradient(90deg, #22d3ee, #6366f1, #ec4899);
          box-shadow: 0 0 12px rgba(99,102,241,0.8);
          transform-origin: left;
          animation: pl-progress 1.9s cubic-bezier(0.65, 0, 0.35, 1) both;
        }

        @keyframes pl-spin { to { transform: rotate(360deg); } }
        @keyframes pl-fade { from { opacity: 0; } to { opacity: 1; } }
        @keyframes pl-pulse { 0%, 100% { opacity: 0.6; transform: scale(0.95); } 50% { opacity: 1; transform: scale(1.05); } }
        @keyframes pl-logo-in {
          from { opacity: 0; transform: scale(0.4) rotate(-20deg); filter: blur(8px); }
          to { opacity: 1; transform: none; filter: drop-shadow(0 0 14px rgba(99,102,241,0.7)); }
        }
        @keyframes pl-letter-in {
          from { opacity: 0; transform: translateY(28px) rotateX(70deg); filter: blur(8px); }
          to { opacity: 1; transform: none; filter: blur(0); }
        }
        @keyframes pl-letter-shine { from { background-position: 100% 50%; } to { background-position: 0% 50%; } }
        @keyframes pl-track {
          from { opacity: 0; letter-spacing: 0.9em; filter: blur(6px); }
          to { opacity: 1; letter-spacing: 0.42em; filter: blur(0); }
        }
        @keyframes pl-up { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
        @keyframes pl-progress { from { transform: scaleX(0); } to { transform: scaleX(1); } }

        @media (prefers-reduced-motion: reduce) {
          .pl-ring, .pl-glow, .pl-logo, .pl-letter, .pl-suffix, .pl-tagline, .pl-bar, .pl-bar > span, .pl-grid {
            animation: none !important;
            opacity: 1;
          }
        }
      `}</style>

      <div className={`page-loading-overlay ${!displayLoading ? 'hidden' : ''}`}>
        <div className="pl-grid" />
        <div className="pl-center">
          <div className="pl-logo-wrap">
            <div className="pl-glow" />
            <div className="pl-ring" />
            <img className="pl-logo" src="https://www.nitminer.com/_next/image?url=%2Fimages%2FLogo%2Flogo.png&w=96&q=75" alt="" />
          </div>

          <div className="pl-name" aria-label="NITMiner Technologies" style={{ perspective: '600px' }}>
            {brand.split('').map((ch, i) =>
              ch === ' ' ? (
                <span key={i} className="pl-space" />
              ) : (
                <span key={i} className="pl-letter" style={{ '--d': `${0.3 + i * 0.06}s` }}>
                  {ch}
                </span>
              )
            )}
          </div>
          <div className="pl-suffix">{suffix}</div>

          <div className="pl-tagline">Automatic Smart Contract Repair</div>

          <div className="pl-bar"><span /></div>
        </div>
      </div>
    </>
  );
}
